import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, Sparkles, Tag, Key, Briefcase, ShoppingBag, Loader2, BadgeCheck } from "lucide-react";

const TABS = [
  { id: "all",             label: "All",        icon: Sparkles },
  { id: "services",        label: "Services",   icon: Sparkles },
  { id: "products",        label: "Products",   icon: ShoppingBag },
  { id: "market_listings", label: "For sale",   icon: Tag },
  { id: "rentals",         label: "Rentals",    icon: Key },
  { id: "jobs",            label: "Jobs",       icon: Briefcase },
];

export default function UserListings() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("all");

  useEffect(() => {
    api.get(`/users/${id}/listings`).then(r => setData(r.data)).catch(e => setErr(formatErr(e)));
  }, [id]);

  if (err) return <div className="max-w-3xl mx-auto p-10"><p className="text-sm text-[#B84B4B]">{err}</p></div>;
  if (!data) return <div className="max-w-3xl mx-auto p-10 text-[#8C8C8C] inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading…</div>;

  const isOwn = user && user.id === id;
  const owner = data.owner || {};
  const counts = data.counts || {};

  const visibleFor = (kind) => (tab === "all" || tab === kind) ? data[kind] || [] : [];

  const totalVisible = tab === "all" ? counts.total : (counts[tab] || 0);

  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-8 py-10" data-testid="user-listings-page">
      <Link to={`/u/${id}`} className="text-sm text-[#454545] inline-flex items-center gap-1 hover:text-[#050505] mb-5">
        <ArrowLeft size={14}/> Back to profile
      </Link>

      <div className="flex items-center gap-4">
        {owner.avatar && <img src={owner.avatar} alt="" className="h-12 w-12 rounded-full object-cover"/>}
        <div>
          <p className="overline">Listings by</p>
          <h1 className="h-display text-3xl inline-flex items-center gap-2">
            {isOwn ? "My listings" : owner.name}
            {owner.verified && <BadgeCheck size={18} className="text-[#050505]"/>}
          </h1>
          <p className="text-sm text-[#8C8C8C] mt-1">{counts.total} active {counts.total === 1 ? "listing" : "listings"}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1.5 mt-8 border-b border-[#E8E8E8] pb-3" data-testid="listings-tabs">
        {TABS.map(({ id: tid, label, icon: Icon }) => {
          const n = tid === "all" ? counts.total : (counts[tid] || 0);
          if (tid !== "all" && n === 0) return null;
          return (
            <button key={tid} onClick={()=>setTab(tid)}
              data-testid={`listings-tab-${tid}`}
              className={`text-sm px-3.5 py-1.5 rounded-full inline-flex items-center gap-1.5 transition-colors ${
                tab === tid ? "bg-[#050505] text-white" : "bg-transparent text-[#454545] hover:bg-[#F5F5F5]"
              }`}>
              <Icon size={13}/>{label} <span className="opacity-60">· {n}</span>
            </button>
          );
        })}
      </div>

      {totalVisible === 0 && (
        <div className="text-center py-16 text-[#8C8C8C]">
          <p className="text-sm">Nothing here yet.</p>
          {isOwn && <Link to="/sell" className="btn-primary mt-4 inline-flex" data-testid="listings-empty-sell">Start selling</Link>}
        </div>
      )}

      {/* Services */}
      {visibleFor("services").length > 0 && (
        <Section title="Services">
          {visibleFor("services").map(s => (
            <Card key={s.id} to={`/u/${id}`} img={s.image} title={s.title} sub={`$${s.price} · ${s.duration_min || 60} min`} badge={s.delivery_type === "online" ? "Online" : null} testid={`listing-service-${s.id}`}/>
          ))}
        </Section>
      )}
      {/* Products */}
      {visibleFor("products").length > 0 && (
        <Section title="Products">
          {visibleFor("products").map(p => (
            <Card key={p.id} to={`/product/${p.id}`} img={(p.images||[])[0]} title={p.title} sub={`$${p.price}`} badge={p.type} testid={`listing-product-${p.id}`}/>
          ))}
        </Section>
      )}
      {/* Market */}
      {visibleFor("market_listings").length > 0 && (
        <Section title="For sale">
          {visibleFor("market_listings").map(m => (
            <Card key={m.id} to={`/market/${m.id}`} img={(m.images||[])[0]} title={m.title} sub={`${m.currency||"USD"} ${m.price} · ${m.condition||""}`} badge={m.location} testid={`listing-market-${m.id}`}/>
          ))}
        </Section>
      )}
      {/* Rentals */}
      {visibleFor("rentals").length > 0 && (
        <Section title="Rentals">
          {visibleFor("rentals").map(r => (
            <Card key={r.id} to={`/rentals/${r.id}`} img={(r.images||[])[0]} title={r.title}
              sub={`${r.currency||"USD"} ${r.daily_rate || r.hourly_rate || r.monthly_rate || 0} · ${r.category}`}
              badge={r.location} testid={`listing-rental-${r.id}`}/>
          ))}
        </Section>
      )}
      {/* Jobs */}
      {visibleFor("jobs").length > 0 && (
        <Section title="Jobs">
          {visibleFor("jobs").map(j => (
            <Card key={j.id} to={`/jobs/${j.id}`} img={null} title={j.title}
              sub={`${j.company_name} · ${j.location || (j.work_type === "remote" ? "Remote" : "—")}`}
              badge={j.work_type === "remote" ? "Remote" : j.work_type === "hybrid" ? "Hybrid" : j.employment_type}
              testid={`listing-job-${j.id}`}/>
          ))}
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-8" data-testid={`listings-section-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <p className="overline mb-3">{title}</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{children}</div>
    </div>
  );
}

function Card({ to, img, title, sub, badge, testid }) {
  return (
    <Link to={to} data-testid={testid} className="card overflow-hidden group hover:border-[#0057FF] transition-colors">
      <div className="aspect-[4/3] bg-[#F5F5F5] relative overflow-hidden">
        {img ? <img src={img} alt="" data-lightbox className="w-full h-full object-cover cursor-zoom-in group-hover:scale-105 transition-transform"/>
              : <div className="w-full h-full flex items-center justify-center text-[#8C8C8C] text-xs">No image</div>}
        {badge && (
          <span className="absolute top-2 left-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-white/95 text-[#050505]">{badge}</span>
        )}
      </div>
      <div className="p-3">
        <p className="text-sm font-medium truncate">{title}</p>
        <p className="text-xs text-[#8C8C8C] truncate mt-0.5">{sub}</p>
      </div>
    </Link>
  );
}
